import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

interface RightsResponse {
  applicationName: string;
  secUserId: number;
  roles: string[];
  functions: string[];
}

/**
 * Loads this user's ForthrightRole/ForthrightFunction rights for IDR from the broker's
 * RightsController — the authorization layer, distinct from authentication (which already
 * succeeded by the time this runs). Called once, right after App's startup checkAuth() resolves.
 * Mirrors DeskApp's own rights.service.ts.
 *
 * A 403 from the broker means this provisioned user has neither an IDR role nor a directly
 * granted function — the broker itself treats that as "no access to this app," not this service's
 * job to interpret an empty list. That's surfaced via `accessDenied`, not `error`, so AuthCallback
 * can tell "you're not allowed in" apart from "the request itself failed."
 */
@Injectable({ providedIn: 'root' })
export class RightsService {
  private readonly http = inject(HttpClient);

  readonly roles = signal<readonly string[]>([]);
  readonly functions = signal<readonly string[]>([]);
  readonly loaded = signal(false);
  readonly accessDenied = signal(false);
  readonly error = signal<string | null>(null);

  load(): void {
    this.loaded.set(false);
    this.roles.set([]);
    this.functions.set([]);
    this.accessDenied.set(false);
    this.error.set(null);
    // No applicationName segment — the broker resolves it from idrportalapp's own configured
    // default (MAX's ForthrightApplication row "IDR"; see ApplicationNameResolver/
    // appsettings.Development.json's SpaClients entry).
    this.http.get<RightsResponse>(`${environment.brokerAuthority}/api/rights`).subscribe({
      next: (response) => {
        this.roles.set(response.roles);
        this.functions.set(response.functions);
        this.loaded.set(true);
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 403) {
          this.accessDenied.set(true);
        } else {
          this.error.set(err instanceof Error ? err.message : 'Failed to load rights.');
        }
        this.loaded.set(true);
      },
    });
  }
}
